/**
 * ============================================================================
 * FILTER CONTROL MODEL — pure token ↔ labelled-control mapping.
 * ============================================================================
 *
 * The control shell (spec filter-control-shell) renders the global
 * `filterQuery` as labelled controls. This module is the ONLY place that
 * translates between control selections and PropertyFilter-shaped tokens, so
 * the shell can never drift from the state semantics the model evaluates:
 * same-property `=` tokens OR (multi-select), properties AND, date bounds AND.
 *
 * Foreign tokens — operators the shell doesn't render (`!=`, `:` from
 * persisted queries) or properties outside the current surface — are always
 * PRESERVED verbatim: editing one control replaces only that property's `=`
 * tokens (or the edited date bound), nothing else.
 */

import { FilterQuery } from 'src/features/dashboard/dashboardSlice';

interface Token {
  propertyKey?: string;
  operator: string;
  value: string;
}

const tokens = (query: FilterQuery): Token[] => (query.tokens ?? []) as Token[];

/** Values of the property's `=` tokens — the control's current selection. */
export function selectedValues(query: FilterQuery, key: string): string[] {
  return tokens(query)
    .filter((t) => t.propertyKey === key && t.operator === '=')
    .map((t) => t.value);
}

/**
 * The query with the property's `=` tokens replaced by the given values
 * (empty values = explicit All = no tokens). Every other token — other
 * properties, and this property's non-`=` tokens — passes through untouched.
 * New tokens append at the end (stable order keeps activeFilterCount honest).
 */
export function withSelectedValues(
  query: FilterQuery,
  key: string,
  values: string[],
): FilterQuery {
  const kept = tokens(query).filter(
    (t) => !(t.propertyKey === key && t.operator === '='),
  );
  const added = values.map((value) => ({
    propertyKey: key,
    operator: '=',
    value,
  }));
  return { ...query, tokens: [...kept, ...added] } as FilterQuery;
}

/** The dcrCreatedDate range bounds ('' = unset). */
export interface DateBounds {
  from: string;
  to: string;
}

const DATE_KEY = 'dcrCreatedDate';

export function dateBounds(query: FilterQuery): DateBounds {
  let from = '';
  let to = '';
  for (const t of tokens(query)) {
    if (t.propertyKey !== DATE_KEY) continue;
    if (t.operator === '>=') from = t.value;
    if (t.operator === '<=') to = t.value;
  }
  return { from, to };
}

/**
 * The query with one date bound set (ISO YYYY-MM-DD) or cleared (''). The
 * other bound and all other tokens are untouched.
 */
export function withDateBound(
  query: FilterQuery,
  bound: 'from' | 'to',
  isoDate: string,
): FilterQuery {
  const operator = bound === 'from' ? '>=' : '<=';
  const kept = tokens(query).filter(
    (t) => !(t.propertyKey === DATE_KEY && t.operator === operator),
  );
  const next = isoDate
    ? [...kept, { propertyKey: DATE_KEY, operator, value: isoDate }]
    : kept;
  return { ...query, tokens: next } as FilterQuery;
}

/** Property keys carrying ANY token — drives the "More filters (N)" badge. */
export function constrainedKeys(query: FilterQuery): Set<string> {
  const keys = new Set<string>();
  for (const t of tokens(query)) {
    if (t.propertyKey) keys.add(t.propertyKey);
  }
  return keys;
}

/**
 * Sentinel TOKEN value for the exclusive-All controls' explicit "nothing
 * selected" state (see FilterControl). Lives here so pure modules (the
 * deep-dive filter adapter) can detect it without importing a component.
 */
export const NOTHING_SELECTED_VALUE = '__nothing__';
